import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, CheckCircle2, Clock, AlertCircle, PartyPopper } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase6Uebergabe = () => {
  const uebergabeSchritte = [
    { titel: 'Kaufpreis-Eingang prüfen', details: 'Käufer überweist Kaufpreis auf Notaranderkonto oder direkt an Verkäufer (je nach Vereinbarung)', pflicht: true },
    { titel: 'Lastenfreistellung durch Bank', details: 'Verkäufer-Bank löscht Grundschuld nach Darlehens-Ablösung (kann 2-4 Wochen dauern)', pflicht: true },
    { titel: 'Eigentumsübergang im Grundbuch', details: 'Notar beantragt Eintragung, Grundbuchamt braucht 4-12 Wochen (regional unterschiedlich)', pflicht: true },
    { titel: 'Übergabeprotokoll erstellen', details: 'Objektbegehung mit Käufer, Zustand dokumentieren, Schlüssel übergeben', pflicht: true },
    { titel: 'Mieter informieren (offiziell)', details: 'Schriftliche Mitteilung: Neuer Eigentümer, neue Bankverbindung für Miete, Ansprechpartner', pflicht: true },
    { titel: 'Nebenkostenabrechnung aufteilen', details: 'Stichtag = Übergabe. Verkäufer rechnet bis Übergabe ab, danach Käufer.', pflicht: true },
    { titel: 'Versicherungen übertragen/kündigen', details: 'Gebäudeversicherung auf Käufer übertragen oder Käufer schließt eigene ab', pflicht: true },
    { titel: 'Steuerliche Meldungen', details: 'Verkauf dem Finanzamt melden (Spekulationsfrist), Grunderwerbsteuer zahlt Käufer', pflicht: true },
    { titel: 'Maklerprovision zahlen', details: 'Falls noch nicht gezahlt: Nach Kaufpreiseingang fällig (üblich 3-4% des Kaufpreises + MwSt)', pflicht: true },
    { titel: 'After-Sale-Support (optional)', details: 'Käufer bei Mieter-Übergabe begleiten, Fragen beantworten, Kontakte vermitteln', pflicht: false }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer (Ex-Verkäufer)', aufgaben: ['Kaufpreis-Eingang bestätigen & Bank informieren', 'Nebenkosten-Abrechnung bis Übergabe erstellen', 'Alle Unterlagen an Käufer übergeben (Verträge, Rechnungen, Kontakte)', 'Mieter persönlich vorstellen (optional, aber empfohlen)', 'Freuen & feiern! 🎉'] },
    { wer: 'RetailInvest', aufgaben: ['Übergabeprotokoll vorbereiten & Termin koordinieren', 'Zwischen Verkäufer & Käufer vermitteln bei letzten Fragen', 'Provision abrechnen & einziehen', 'Nachbetreuung: Check nach 3 Monaten (alles läuft?)', 'Referenz-Bestätigung anfragen (für zukünftige Mandate)'] },
    { wer: 'Käufer (Neu-Eigentümer)', aufgaben: ['Kaufpreis pünktlich zahlen', 'Versicherungen abschließen (Gebäude, Haftpflicht)', 'Mieter kontaktieren & Vertrauen aufbauen', 'Grundbucheintrag überwachen', 'Hausverwaltung beauftragen (falls extern)'] }
  ];

  const typischeProbleme = [
    { problem: 'Grundbucheintrag dauert 6+ Monate (statt 4-8 Wochen)', loesung: 'Kein Problem! Mit Kaufpreiszahlung ist Käufer wirtschaftlicher Eigentümer (erhält Miete, trägt Risiko). Grundbuch ist nur formaler Akt. Bei Dringlichkeit: Notar kann Grundbuchamt "anstupsen".' },
    { problem: 'Mieter ist verunsichert durch Eigentümerwechsel ("Wird mein Vertrag gekündigt?")', loesung: 'Verkäufer & Käufer gemeinsam Mieter besuchen → Vertrauen aufbauen. Schriftlich bestätigen: "Mietvertrag läuft unverändert weiter". Käufer sollte persönlich vorstellen & Visitenkarte dalassen.' },
    { problem: 'Verkäufer hat vergessen, Nebenkosten bis Übergabe abzurechnen → Käufer verlangt Nachzahlung', loesung: 'Im Kaufvertrag sollte Stichtag geregelt sein. Falls nicht: Verkäufer muss Abrechnung nachreichen. Typische Nachzahlung: € 2.000-5.000 (je nach Objektgröße).' },
    { problem: 'Käufer findet nach Übergabe "versteckte Mängel" und droht mit Klage', loesung: 'Kaufvertrag sollte Haftungsausschluss enthalten ("gekauft wie besichtigt"). Falls nicht: Verkäufer nur haftbar bei arglistiger Täuschung. Übergabeprotokoll mit Fotos schützt!' }
  ];

  const abschlussChecklist = [
    { titel: 'Finanziell', punkte: ['Kaufpreis vollständig erhalten?', 'Darlehen bei Bank abgelöst?', 'Maklerprovision gezahlt?', 'Notarrechnung beglichen?', 'Steuerberater informiert (Gewinn versteuern)?'] },
    { titel: 'Rechtlich', punkte: ['Grundbucheintrag beantragt?', 'Versicherungen gekündigt/übertragen?', 'Mieter offiziell informiert?', 'Alle Unterlagen an Käufer übergeben?', 'Übergabeprotokoll unterschrieben?'] },
    { titel: 'Persönlich', punkte: ['Käufer persönlich kennengelernt?', 'Mieter dem Käufer vorgestellt?', 'Letzte offene Fragen geklärt?', 'Referenz-Bestätigung für Makler gegeben?', 'Erfolg gefeiert? 🥂'] }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 6: Übergabe & After Sale – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 6: Kaufpreiszahlung, Grundbucheintrag, Übergabeprotokoll, Mieter-Information, Nebenkosten, After-Sale." url="/ablauf/phase-6-uebergabe" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
              <span className="text-4xl font-bold text-white">06</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#0F2744] to-[#2563A0] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Übergabe & After Sale</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 4-12 Wochen (Grundbucheintrag)</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <PartyPopper className="h-12 w-12" style={{ color: 'var(--gold-600)' }} />
              <h2 className="text-3xl font-bold" style={{ color: 'var(--navy-800)' }}>Geschafft! Fast am Ziel.</h2>
            </div>
            <p className="text-lg mb-4 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Mit Kaufpreiszahlung ist der Verkauf faktisch abgeschlossen.</strong> Der Grundbucheintrag ist nur noch Formalität – Sie sind rechtlich & wirtschaftlich aus der Immobilie raus. Jetzt gilt es, die letzten administrativen Schritte sauber abzuwickeln und dem Käufer einen professionellen Übergang zu ermöglichen.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Ein zufriedener Käufer ist Ihr bestes Marketing.</strong> Empfehlungen aus dem Netzwerk sind Gold wert – behandeln Sie den Käufer so, wie Sie selbst behandelt werden möchten. Ein 30-minütiges Mieter-Treffen zu dritt (Verkäufer, Käufer, Mieter) verhindert 90% aller Nach-Verkaufs-Probleme.
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Übergabe-Checkliste</h2>
            <div className="space-y-4">
              {uebergabeSchritte.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: s.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{s.titel}</h3>
                      {s.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{s.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Abschluss-Checkliste (Alle Punkte abgehakt?)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {abschlussChecklist.map((c, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{c.titel}</h3>
                  <ul className="space-y-2">
                    {c.punkte.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Expertentipp: Professioneller Abschluss</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>✓ Do's</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Mieter & Käufer persönlich vorstellen (30-Min-Treffen vor Ort)</li>
                  <li>• Übergabeprotokoll mit Fotos dokumentieren (Beweissicherung)</li>
                  <li>• Alle Kontakte weitergeben (Handwerker, Verwalter, Energieversorger)</li>
                  <li>• Referenz-Bestätigung für Makler schreiben (2-3 Sätze genügen)</li>
                  <li>• Nach 3 Monaten nachfragen: "Alles läuft?" (Goodwill++)</li>
                </ul>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--error-700)' }}>✗ Don'ts</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Käufer "alleine lassen" (kein Kontakt nach Übergabe)</li>
                  <li>• Mieter negativ über Käufer reden ("Der wird alles ändern...")</li>
                  <li>• Unterlagen unvollständig übergeben (Käufer muss nachfragen)</li>
                  <li>• Ohne Übergabeprotokoll übergeben (Haftungsrisiko!)</li>
                  <li>• Maklerprovision verzögern (Vertrauen zerstören)</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8" style={{ background: 'linear-gradient(135deg, rgba(212, 168, 83, 0.1) 0%, rgba(240, 200, 112, 0.1) 100%)', border: '2px solid var(--gold-600)' }}>
            <div className="text-center">
              <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--gold-600)' }} />
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>Herzlichen Glückwunsch zum erfolgreichen Verkauf!</h2>
              <p className="text-lg mb-6" style={{ color: 'var(--gray-700)' }}>
                Sie haben Ihre Handelsimmobilie professionell verkauft. Falls Sie in Zukunft weitere Objekte veräußern möchten – wir stehen Ihnen jederzeit zur Verfügung.
              </p>
              <Link to="/kontakt">
                <Button className="btn-gold-gradient text-white border-0 font-semibold py-6 px-8" data-testid="contact-cta">
                  Nächstes Objekt verkaufen
                </Button>
              </Link>
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf/phase-5-notar" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zu Phase 5
              </Button>
            </Link>
            <Link to="/ablauf" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Zurück zur Übersicht
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase6Uebergabe;
